'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { useState, useEffect } from 'react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [applicationCategories, setApplicationCategories] = useState<any[]>([]);
  const [siteName, setSiteName] = useState('');
  const [siteDescription, setSiteDescription] = useState('');
  const [siteLogo, setSiteLogo] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [copyrightInfo, setCopyrightInfo] = useState('');
  const [icpNumber, setIcpNumber] = useState('');
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [loading, setLoading] = useState(true);

  // 获取应用范围子栏目数据、网站名称和网站描述
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 获取应用范围子栏目数据
        const categoriesResponse = await fetch('/api/categories');
        const categoriesResult = await categoriesResponse.json();
        
        if (categoriesResult.success && categoriesResult.data) {
          // 筛选应用范围栏目（ID为2）下的子栏目，并按sortOrder排序
          const appCategories = categoriesResult.data
            .filter((cat: any) => cat.parentId === 2 && cat.isActive)
            .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));
          
          setApplicationCategories(appCategories);
        }
        
        // 获取网站名称
        const settingsResponse = await fetch('/api/settings');
        const settingsResult = await settingsResponse.json();
        
        if (settingsResult.success && settingsResult.data) {
          const siteNameSetting = settingsResult.data.find((setting: any) => setting.key === 'site_name');
          if (siteNameSetting) {
            setSiteName(siteNameSetting.value);
          }
        }
        
        // 获取网站描述、Logo和二维码图片（从home_page_settings表）
        const homepageResponse = await fetch('/api/homepage');
        const homepageResult = await homepageResponse.json();
        
        if (homepageResult.success && homepageResult.data) {
          if (homepageResult.data.siteDescription) {
            setSiteDescription(homepageResult.data.siteDescription);
          }
          if (homepageResult.data.siteLogo) {
            setSiteLogo(homepageResult.data.siteLogo);
          }
          if (homepageResult.data.qrCodeImage) {
            setQrCodeImage(homepageResult.data.qrCodeImage);
          }
        }
        
        // 获取联系信息和版权信息
        if (settingsResult.success && settingsResult.data) {
          const contactPhoneSetting = settingsResult.data.find((setting: any) => setting.key === 'contact_phone');
          if (contactPhoneSetting) {
            setContactPhone(contactPhoneSetting.value);
          }
          
          const contactEmailSetting = settingsResult.data.find((setting: any) => setting.key === 'contact_email');
          if (contactEmailSetting) {
            setContactEmail(contactEmailSetting.value);
          }
          
          const companyAddressSetting = settingsResult.data.find((setting: any) => setting.key === 'company_address');
          if (companyAddressSetting) {
            setCompanyAddress(companyAddressSetting.value);
          }
          
          const copyrightInfoSetting = settingsResult.data.find((setting: any) => setting.key === 'copyright_info');
          if (copyrightInfoSetting) {
            setCopyrightInfo(copyrightInfoSetting.value);
          }
          
          const icpNumberSetting = settingsResult.data.find((setting: any) => setting.key === 'icp_number');
          if (icpNumberSetting) {
            setIcpNumber(icpNumberSetting.value);
          }
        }
      } catch (error) {
        console.error('获取数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 使用实际数据或默认数据
  const footerSections = loading || applicationCategories.length === 0 
    ? [
        { name: '航空航天', id: 'satellite' },
        { name: '智能汽车', id: 'camera' },
        { name: '精密仪器', id: 'optical-instrument' },
        { name: '光谱仪', id: 'spectrometer' },
        { name: '激光雷达', id: 'lidar' },
        { name: '汽车摄像头', id: 'camera' },
        { name: 'PCBA板', id: 'camera-pcba' },
        { name: '摄像头支架', id: 'camera-bracket' },
        { name: '儿童玩具', id: 'toys' },
      ]
    : applicationCategories.map(cat => ({
        name: cat.name,
        id: cat.slug
      }));

  const socialLinks = [
    { name: '微博', icon: '🐦', href: '#' },
    { name: '领英', icon: '💼', href: '#' },
    { name: '微信', icon: '💬', href: '#' }
  ];

  const certifications = [
    { name: 'ISO 9001', icon: '🏆' },
    { name: '高新技术企业', icon: '🌟' },
    { name: 'CE认证', icon: '✅' }
  ];

  return (
    <footer className="bg-gradient-to-t from-black via-gray-900 to-transparent border-t border-white/10 backdrop-blur-sm relative overflow-hidden">
      {/* 背景装饰元素 */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/40"></div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-orange-500/30 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* 主要区域 */}
        <div className="py-5">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            {/* 品牌区域 - 左侧 */}
            <div className="space-y-4 text-center">
              <div className="flex items-center justify-center space-x-3">
                {siteLogo ? (
                  <img 
                    src={siteLogo} 
                    alt={siteName}
                    className="w-10 h-10 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg">S</span>
                  </div>
                )}
                <h2 className="text-white text-xl font-bold">{siteName}</h2>
              </div>
              <p className="text-gray-400 text-lg leading-relaxed">
                {siteDescription}
              </p>
            </div>
          </div>

          {/* 应用范围子栏目 - 中间 */}
          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {footerSections.map((category, index) => (
                <motion.div
                  key={category.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                >
                  <Link 
                      href={`/applications/${category.id}`}
                      className="text-gray-300 hover:text-white transition-all duration-300 text-base font-medium block py-3 px-4 bg-white/10 hover:bg-white/20 rounded-lg text-center border border-white/10 hover:border-white/30 shadow-lg hover:shadow-xl whitespace-nowrap overflow-hidden text-ellipsis"
                    >
                      {category.name}
                    </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* 联系信息 - 右侧 */}
          <div className="lg:col-span-3">
            <div className="space-y-4 text-center">
              <h3 className="text-white font-semibold text-xl">联系我们</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-center space-x-3 text-gray-400 hover:text-white transition-colors duration-300">
                  <span className="text-white text-lg">📞</span>
                  <span className="text-lg">{contactPhone}</span>
                </div>
                <div className="flex items-center justify-center space-x-3 text-gray-400 hover:text-white transition-colors duration-300">
                  <span className="text-white text-lg">✉️</span>
                  <span className="text-lg">{contactEmail}</span>
                </div>
                <div className="flex items-center justify-center space-x-3 text-gray-400 hover:text-white transition-colors duration-300">
                  <span className="text-white text-lg">📍</span>
                  <span className="text-lg">{companyAddress}</span>
                </div>
              </div>
              {/* 二维码图片 */}
              {qrCodeImage && (
                <div className="pt-2">
                  <div className="flex justify-center">
                    <img 
                      src={qrCodeImage} 
                      alt="二维码"
                      className="w-28 h-28 object-cover rounded-lg border border-white/20 shadow-lg"
                    />
                  </div>
                  <p className="text-gray-400 text-lg mt-1">扫码联系我们</p>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
        
        {/* 分割线 */}
        <div className="border-t border-white/10 relative">
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-orange-500 to-transparent"></div>
        </div>
        
        {/* 底部版权区域 */}
        <div className="py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-4"
          >
            <div className="text-gray-400 text-base">
            {copyrightInfo}
            </div>
            <div className="flex items-center justify-center space-x-6">
              <a 
                href="https://beian.miit.gov.cn/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-gray-300 transition-all duration-300 text-sm border border-white/10 hover:border-white/20 px-3 py-1 rounded-lg"
              >
                {icpNumber}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;